/**
 * Logger class for structured event logging.
 *
 * The Logger combines a backend with a context provider to create fully
 * enriched log entries with auto-injected core and context fields.
 */

import { ConsoleLogBackend } from "./backends/console";
import type { LogBackend } from "./backends/interface";
import {
  type ContextProvider,
  createCoreFields,
  createEmptyContextProvider,
  type CoreFieldsInput,
} from "./core-fields";
import { toLogError } from "./log-error";
import type { BaseLogEntry, EventName, LogError, LogLevel } from "./types";

export class Logger {
  private backend: LogBackend;
  private contextProvider: ContextProvider;
  private coreFieldsInput: CoreFieldsInput;
  private debugEnabled: boolean;
  private fallbackBackend: ConsoleLogBackend | null = null;

  /**
   * Create a new Logger instance.
   *
   * @param backend - The log backend to write events to
   * @param contextProvider - Optional provider for session/user context
   * @param coreFieldsInput - service/environment/version to stamp on every event
   * @param debugEnabled - Gate for debug(); a plain boolean rather than an
   *   env read, since this package stays environment-agnostic (see
   *   core-fields.ts) — the caller (apps/retells/src/logging/) reads
   *   VITE_LOG_DEBUG and passes the result in.
   */
  constructor(
    backend: LogBackend,
    contextProvider: ContextProvider | undefined,
    coreFieldsInput: CoreFieldsInput,
    debugEnabled = false,
  ) {
    this.backend = backend;
    this.contextProvider = contextProvider ?? createEmptyContextProvider();
    this.coreFieldsInput = coreFieldsInput;
    this.debugEnabled = debugEnabled;
  }

  /**
   * Log an event with optional data.
   *
   * Core fields (eventId, timestamp, environment, service, version) are auto-injected.
   * Context fields (sessionId, userId) are injected from the context provider.
   *
   * @param eventName - The event name in domain.action format
   * @param data - Optional event-specific data
   */
  event(eventName: EventName, data?: Record<string, unknown>): void {
    const entry = this.buildEntry("info", eventName, data);
    this.writeWithFallback(entry);
  }

  /**
   * Log an error event.
   *
   * Converts Error objects to the LogError structure.
   * Core and context fields are auto-injected.
   *
   * @param eventName - The event name in domain.action format
   * @param error - Error object or LogError structure
   * @param data - Optional additional event-specific data
   */
  error(
    eventName: EventName,
    error: unknown,
    data?: Record<string, unknown>,
  ): void {
    const logError = this.normalizeError(error);
    const entry = this.buildEntry("error", eventName, data, logError);
    this.writeWithFallback(entry);
  }

  /**
   * Log a debug event.
   *
   * Gated by the `debugEnabled` constructor param. When it's false, this is
   * a no-op — no entry is built and no backend is called. Intended for
   * tracing instrumentation that should be toggleable per-environment
   * without removing call sites.
   *
   * Never use for errors; errors must always flow through `error()`.
   *
   * @param eventName - The event name in domain.action format
   * @param data - Optional event-specific data
   */
  debug(eventName: EventName, data?: Record<string, unknown>): void {
    if (!this.debugEnabled) return;
    const entry = this.buildEntry("debug", eventName, data);
    this.writeWithFallback(entry);
  }

  /**
   * Build a complete log entry with all fields.
   */
  private buildEntry(
    level: LogLevel,
    eventName: string,
    data?: Record<string, unknown>,
    error?: LogError,
  ): BaseLogEntry {
    const coreFields = createCoreFields(this.coreFieldsInput);

    return {
      // Core fields
      ...coreFields,
      // Context fields
      sessionId: this.contextProvider.getSessionId(),
      userId: this.contextProvider.getUserId(),
      // Optional on the interface: a provider that predates these fields, or
      // an app with no reader identity and no campaigns, simply omits them.
      readerId: this.contextProvider.getReaderId?.(),
      leadId: this.contextProvider.getLeadId?.(),
      // Event data
      level,
      eventName,
      data,
      error,
    };
  }

  /**
   * Write to primary backend with console fallback on failure.
   */
  private writeWithFallback(entry: BaseLogEntry): void {
    try {
      this.backend.write(entry);
    } catch {
      // Fall back to console if primary backend fails
      this.fallbackBackend ??= new ConsoleLogBackend();
      try {
        this.fallbackBackend.write(entry);
      } catch {
        // If even console fails, silently give up
      }
    }
  }
  /** Normalize a thrown value — shared with Convex; see ./log-error.ts. */
  private normalizeError(error: unknown): LogError {
    return toLogError(error);
  }

  /**
   * Flush the backend if it supports flushing.
   * Call before app shutdown to ensure all events are sent.
   */
  async flush(): Promise<void> {
    if (this.backend.flush) {
      await this.backend.flush();
    }
  }
}
